/**
 * Persist / restore App workspace tabs per connection.
 * Hydrate once per connection id — never re-reset on navigate identity churn.
 * Records start unresolved (openMode re-resolves); lists/reports open as tabs.
 */
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useConnection } from '@/app/contexts/ConnectionContext'
import { prefsStore } from '@/app/preferences/prefsStore'
import { attachBeforeUnloadGuard } from '@/designer/utils/beforeUnloadGuard'
import { useAppWorkspaceStore, type AppTab, type AppTabInput } from './appWorkspaceStore'
import { isAppDataPath } from './isAppDataPath'
import { loadTabSession, saveTabSession } from './tabSession'
import { workspaceHasDirtyTabs } from './confirmCloseWorkspaceTab'

function toPersisted(tabs: AppTab[]) {
  return tabs.map(({ path, kind, title }) => ({ path, kind, title }))
}

function presentationForKind(kind: AppTab['kind']): AppTab['presentation'] {
  return kind === 'record' ? 'unresolved' : 'tab'
}

export function useAppTabSession(): void {
  const { selectedConnection } = useConnection()
  const navigate = useNavigate()
  const location = useLocation()
  const cid = selectedConnection?.id ?? null
  const hydratingRef = useRef(false)
  const hydratedForCidRef = useRef<string | null>(null)
  const locationRef = useRef(location)
  locationRef.current = location
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  // Restore on connection ready / switch — once per cid.
  useEffect(() => {
    if (!cid) {
      useAppWorkspaceStore.getState().reset()
      hydratedForCidRef.current = null
      hydratingRef.current = false
      return
    }
    if (hydratedForCidRef.current === cid) {
      // StrictMode re-runs this effect and cleanup cancels the timeout that
      // clears hydratingRef. Returning here used to leave the flag stuck, so
      // later closes never persisted and the next login restored them.
      hydratingRef.current = false
      return
    }
    hydratedForCidRef.current = cid

    hydratingRef.current = true
    const session = loadTabSession(cid)
    if (session && session.tabs.length > 0) {
      const tabs: AppTabInput[] = session.tabs.map((t) => ({
        path: t.path,
        kind: t.kind,
        title: t.title,
        isDirty: false,
        presentation: presentationForKind(t.kind),
      }))
      useAppWorkspaceStore.getState().hydrate(tabs, session.activePath)
      // Don't steal a deep-link; only jump when the user is on a service screen.
      if (session.activePath && !isAppDataPath(locationRef.current.pathname)) {
        navigateRef.current(session.activePath)
      }
    } else {
      // Empty session: do NOT reset if a WorkspaceRoute already opened a tab
      // this tick (child effects run before this parent effect). Wiping left
      // /app/:object deep-links permanently blank — route effect does not re-fire.
      if (useAppWorkspaceStore.getState().tabs.length === 0) {
        useAppWorkspaceStore.getState().reset()
      }
    }
    const t = window.setTimeout(() => {
      hydratingRef.current = false
    }, 0)
    return () => {
      window.clearTimeout(t)
    }
  }, [cid])

  const tabs = useAppWorkspaceStore((s) => s.tabs)
  const activePath = useAppWorkspaceStore((s) => s.activePath)

  // Debounced persist — skip while hydrating to avoid wiping storage with [].
  // An empty strip is written immediately: logout clears the auth token before
  // the prefs flush, so a pending debounce would leave the previous tabs on
  // the server and the next login would restore what the user closed.
  useEffect(() => {
    if (!cid || hydratingRef.current) return
    const persist = () => {
      if (hydratingRef.current) return
      const live = useAppWorkspaceStore.getState()
      saveTabSession(cid, {
        tabs: toPersisted(live.tabs),
        activePath: live.activePath,
      })
      if (live.tabs.length === 0) prefsStore.flushNow()
    }
    if (useAppWorkspaceStore.getState().tabs.length === 0) {
      persist()
      return
    }
    const handle = window.setTimeout(persist, 100)
    return () => window.clearTimeout(handle)
  }, [cid, tabs, activePath])

  useEffect(() => attachBeforeUnloadGuard(() => workspaceHasDirtyTabs()), [])
}
